import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AdminsApi,
  AdminsFinanceCreateVendorRequest,
  AdminsFinanceGetBankListResponseItem,
  AdminsFinanceGetVendorListResponseItem,
  AdminsFinanceUpdateVendorRequest,
  Configuration,
  LocationApi,
  ProvinceResponseItem,
  StatusBadRequestErrorResponse,
  TaxTypeEnum,
  VendorDirectionEnum,
  VendorSortFieldEnum,
  VendorStatusEnum,
  VendorTypeEnum,
} from 'generated/api'
import { encryptAsRsa } from 'helpers/authentification'
import Toast from 'components/toast/Toast'
import { AxiosError } from 'axios'
import { axiosDefaultConfig } from 'constants/axios'

const axiosConfig = new Configuration(axiosDefaultConfig)
const adminsApi = new AdminsApi(axiosConfig)
const locationApi = new LocationApi(axiosConfig)

export const useVendors = () => {
  const [stateToast, setStateToast] = useState<'create-success-vendor' | 'edit-success-vendor'>(
    'create-success-vendor',
  )
  const { toast, toastContainer } = Toast()
  const navigate = useNavigate()
  const [statusFetchVendors, setStatusFetchVendors] = useState<'loading' | 'success' | 'failed'>(
    'loading',
  )
  const [vendors, setVendors] = useState<AdminsFinanceGetVendorListResponseItem[]>([])
  const [page, setPage] = useState<number | undefined>(1)
  const [totalRows, setTotalRows] = useState<number | undefined>(0)
  const [limit, setLimit] = useState(10)
  const [status, setStatus] = useState<VendorStatusEnum>('' as VendorStatusEnum)
  const [type, setType] = useState<VendorTypeEnum>('' as VendorTypeEnum)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [sortField, setSortField] = useState<VendorSortFieldEnum>('' as VendorSortFieldEnum)
  const [sortDirection, setSortDirection] = useState<VendorDirectionEnum>('' as VendorDirectionEnum)
  const [stateForm, setStateForm] = useState<'create' | 'edit'>('create')
  const [statusForm, setStatusForm] = useState<'loading' | 'success' | 'idle' | 'failed'>('idle')
  const [initialForm, setInitialForm] = useState<AdminsFinanceCreateVendorRequest>({
    name: '',
    type: VendorTypeEnum.FarmerStore,
    ownerName: '',
    ownerPhoneNo: '',
    picName: '',
    picPhoneNo: '',
    province: '',
    city: '',
    address: '',
    taxType: '' as TaxTypeEnum,
    bankId: '',
    bankAccountNo: '',
    bankAccountName: '',
    fleetType: '',
    latitude: 0,
    longitude: 0,
    isFleet: false,
    isPC: false,
    isTax: false,
    isWarehouse: false,
    isWifi: false,
    mouFilePath: '',
    savingBookFilePath: '',
    status: '',
    taxIdentityNo: '',
  })
  const [provincesAndCities, setProvincesAndCities] = useState<ProvinceResponseItem[]>([])
  const [banks, setBanks] = useState<AdminsFinanceGetBankListResponseItem[]>([])
  const [isInitFetchBanks, setInitFetchBanks] = useState(true)
  const [pageBanks, setPageBanks] = useState(1)
  const [totalPageBanks, setTotalPageBanks] = useState(0)
  const [vendorId, setVendorId] = useState<string>('' as string)

  useEffect(() => {
    if (location.pathname === '/inventory/vendors') {
      handleFetchVendors()
    }
  }, [page, limit, status, type, sortField, sortDirection])

  useEffect(() => {
    if (!isInitFetchBanks) handleFetchBanks()
  }, [pageBanks])

  const handleChangePage = (page: number) => {
    setPage(page)
  }

  const handleChangeLimit = (limit: number) => {
    setLimit(limit)
    setPage(1)
  }

  const handleChangeStatus = (status: VendorStatusEnum) => {
    setStatus(status)
    setPage(1)
  }

  const handleChangeType = (type: VendorTypeEnum) => {
    setType(type)
    setPage(1)
  }

  const handleChangeSearchKeyword = (keyword: string) => {
    setSearchKeyword(keyword)
  }

  const handleOnSearchKeyword = () => {
    if (page === 1) handleFetchVendors()
    else setPage(1)
  }

  const setSortingVendor = (field: VendorSortFieldEnum) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSortField(field)
      setSortDirection('ASC')
    }
  }

  const handleFetchVendors = async () => {
    try {
      setStatusFetchVendors('loading')
      const { data } = await adminsApi.adminsFinanceGetVendorList(
        page,
        limit,
        status ? status : undefined,
        searchKeyword ? searchKeyword : undefined,
        type ? type : undefined,
        sortField ? sortField : undefined,
        sortDirection ? sortDirection : undefined,
      )
      setVendors(data.items)
      setTotalRows(data.pagination?.total)
      setStatusFetchVendors('success')
    } catch {
      setStatusFetchVendors('failed')
    }
  }

  const handleFetchOnCreate = async () => {
    try {
      setStatusForm('loading')
      setStateForm('create')
      await Promise.all([handleFetchProvincesAndCities(), handleFetchBanks()])
      setStatusForm('idle')
    } catch {
      setStatusForm('failed')
    }
  }

  const handleFetchOnEdit = async (id: string) => {
    try {
      setStatusForm('loading')
      setStateForm('edit')
      setVendorId(id)
      const [vendorDetail] = await Promise.all([
        adminsApi.adminsFinanceGetVendorDetail(id),
        handleFetchProvincesAndCities(),
        handleFetchBanks(),
      ])
      setInitialForm({
        name: vendorDetail.data.name,
        type: vendorDetail.data.type,
        ownerName: vendorDetail.data.ownerName,
        ownerPhoneNo: vendorDetail.data.ownerPhoneNo,
        picName: vendorDetail.data.picName,
        picPhoneNo: vendorDetail.data.picPhoneNo,
        province: vendorDetail.data.province,
        city: vendorDetail.data.city,
        address: vendorDetail.data.address,
        taxType: vendorDetail.data.taxType,
        bankId: vendorDetail.data.bankId,
        bankAccountNo: '',
        bankAccountName: vendorDetail.data.bankAccountName,
        fleetType: vendorDetail.data.fleetType,
        latitude: vendorDetail.data.longitude,
        longitude: vendorDetail.data.latitude,
        isFleet: vendorDetail.data.isFleet,
        isPC: vendorDetail.data.isPC,
        isTax: vendorDetail.data.isTax,
        isWarehouse: vendorDetail.data.isWarehouse,
        isWifi: vendorDetail.data.isWifi,
        mouFilePath: vendorDetail.data.mouFilePath,
        savingBookFilePath: vendorDetail.data.savingBookFilePath,
        status: vendorDetail.data.status,
        taxIdentityNo: '',
      })
      setStatusForm('idle')
    } catch {
      setStatusForm('failed')
    }
  }

  const handleFetchBanks = async () => {
    const { data } = await adminsApi.adminsFinanceGetBankList(pageBanks, 10)
    setBanks(banks.concat(data.items))
    setTotalPageBanks(data.pagination?.pages as number)
    setInitFetchBanks(false)
  }

  const handleFetchProvincesAndCities = async () => {
    const { data } = await locationApi.locationGetProvince('')
    setProvincesAndCities(data.items)
  }

  const handleChangePageBanks = (page: number) => {
    setPageBanks(page)
  }

  const handleSubmit = async (value: AdminsFinanceCreateVendorRequest) => {
    try {
      setStatusForm('loading')
      const encryptTaxIdentityId = encryptAsRsa(value.taxIdentityNo?.toString() as string)
      const encryptBankAccountNo = encryptAsRsa(value.bankAccountNo?.toString() as string)
      const payload: AdminsFinanceCreateVendorRequest | AdminsFinanceUpdateVendorRequest = {
        name: value.name,
        type: value.type,
        ownerName: value.ownerName,
        ownerPhoneNo: value.ownerPhoneNo,
        ...(value.picName && { picName: value.picName }),
        ...(value.picPhoneNo && { picPhoneNo: value.picPhoneNo }),
        province: value.province,
        city: value.city,
        address: value.address,
        ...(value.taxType && { taxType: value.taxType }),
        ...(value.taxIdentityNo && { taxIdentityNo: encryptTaxIdentityId }),
        ...(value.bankId && { bankId: value.bankId }),
        ...(value.bankAccountNo && { bankAccountNo: encryptBankAccountNo }),
        ...(value.bankAccountName && { bankAccountName: value.bankAccountName }),
        ...(value.mouFilePath && { mouFilePath: value.mouFilePath }),
        ...(value.savingBookFilePath && { savingBookFilePath: value.savingBookFilePath }),
        isFleet: value.isFleet,
        isPC: value.isPC,
        isTax: value.isTax,
        longitude: value.longitude,
        latitude: value.latitude,
        isWarehouse: value.isWarehouse,
        isWifi: value.isWifi,
        ...(value.fleetType && value.isFleet && { fleetType: value.fleetType }),
        status: 'ACTIVE',
      }

      if (stateForm === 'create') {
        await adminsApi.adminsFinanceCreateVendor(payload as AdminsFinanceCreateVendorRequest)
      } else if (stateForm === 'edit') {
        await adminsApi.adminsFinanceUpdateVendor(
          vendorId,
          payload as AdminsFinanceUpdateVendorRequest,
        )
      }
      const updatedToast = stateForm === 'create' ? 'create-success-vendor' : 'edit-success-vendor'
      setStateToast(updatedToast)
      setStatusForm('success')
      navigate('/inventory/vendors', {
        state: {
          name: value.name,
          actionType: updatedToast,
        },
        replace: true,
      })
    } catch (err) {
      setStatusForm('failed')
      const error = err as AxiosError
      const responseStatus = error.response?.status
      const errorMessage = error.response?.data as StatusBadRequestErrorResponse
      if (responseStatus && responseStatus >= 400 && responseStatus < 500) {
        await toast(errorMessage.error, 'danger')
      } else {
        await toast('Tidak dapat terhubung ke server, silakan mencoba kembali.', 'danger')
      }
    }
  }

  return {
    banks,
    handleChangeLimit,
    handleChangePage,
    handleChangePageBanks,
    handleChangeSearchKeyword,
    handleChangeStatus,
    handleChangeType,
    handleFetchBanks,
    handleFetchProvincesAndCities,
    handleFetchOnCreate,
    handleFetchOnEdit,
    handleFetchVendors,
    handleOnSearchKeyword,
    handleSubmit,
    initialForm,
    limit,
    page,
    pageBanks,
    provincesAndCities,
    searchKeyword,
    setSortingVendor,
    sortDirection,
    sortField,
    stateToast,
    status,
    statusFetchVendors,
    stateForm,
    statusForm,
    toast,
    toastContainer,
    totalPageBanks,
    totalRows,
    type,
    vendors,
  }
}
